/**
 * Changelog Data
 * Version history with release notes for "What's New" modal
 */

export interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  highlights: string[];
  features?: {
    title: string;
    description: string;
    icon?: string;
  }[];
  improvements?: string[];
  bugfixes?: string[];
}

export const CURRENT_VERSION = '0.69';

export const changelog: ChangelogEntry[] = [
  {
    version: '0.69',
    date: '2026-07-10T17:55:00+03:00',
    title: 'Известия, бързо навигиране, следене на теми и по-сигурна платформа',
    highlights: [
      'Камбанка за известия със споменавания директно в заглавната лента',
      'Бързо навигиране до всеки канал, ЛС или човек с Ctrl+K',
      'Следвайте теми и получавайте известия за нови отговори',
      'Смяна и възстановяване на парола, търсене на хора и канали',
    ],
    features: [
      {
        title: 'Известия със споменавания',
        description: 'Нова камбанка в заглавната лента показва кога някой ви е споменал. Виждате брой непрочетени, списък с последните споменавания и отивате директно до съобщението с едно кликване.',
        icon: '🔔',
      },
      {
        title: 'Бързо навигиране (Ctrl+K)',
        description: 'Натиснете Ctrl+K (или Cmd+K), за да скочите мигновено до всеки канал, лично съобщение или човек чрез търсене с клавиатурата. Избирането на човек отваря разговор с него.',
        icon: '⌨️',
      },
      {
        title: 'Следене на теми',
        description: 'Следвайте интересна тема и получавайте известие при всеки нов отговор в нея, без да я отваряте постоянно.',
        icon: '🧵',
      },
      {
        title: 'Смяна и възстановяване на парола',
        description: 'Сменете паролата си от настройките за сигурност или я възстановете чрез имейл, ако сте я забравили.',
        icon: '🔑',
      },
      {
        title: 'Търсене на хора и канали',
        description: 'Глобалното търсене вече има раздели „Хора" и „Канали" — намерете колега или канал по име и отидете директно там.',
        icon: '🔍',
      },
      {
        title: 'Позоваване на канали с #',
        description: 'Напишете # в съобщение, за да споменете канал. Показва се автоматично довършване, а споменатият канал става кликаем линк.',
        icon: '#️⃣',
      },
      {
        title: 'Команди с /',
        description: 'Напишете „/" в полето за писане, за да видите и изпълните бързи команди чрез автоматично довършване.',
        icon: '⚡',
      },
      {
        title: 'Галерия за прикачени файлове',
        description: 'Снимките и видеата в съобщенията вече се показват в подредена галерия с преглед на цял екран.',
        icon: '🖼️',
      },
      {
        title: 'Чернови на съобщения',
        description: 'Недовършените съобщения се запазват отделно за всеки разговор и се възстановяват, когато се върнете към него.',
        icon: '📝',
      },
      {
        title: 'Постоянен статус и разделител за нови съобщения',
        description: 'Вашият статус вече се запазва и се вижда от колегите в списъка с членове, ЛС картите и профила. В разговорите се появява разделител „Нови съобщения" с бутон за прескачане до тях.',
        icon: '🟢',
      },
    ],
    improvements: [
      'Избор на тема Светла / Тъмна / Системна в настройките за външен вид',
      'Банер при прекъсната връзка в чата и автоматично повторно свързване',
      'Заглавието на страницата показва активния разговор и броя непрочетени',
      'Страница 404 и защита срещу срив с възможност за възстановяване',
      'Достъпност: съобщения за грешки към екранни четци, навигация с клавиатура в диалози, табове и падащи менюта',
      'Настройките за поверителност и известия вече работят в тъмна тема и са на български',
      'Бутонът за емоджи в полето за писане вече отваря избора на емоджи',
      'Създаването на канал вече го отваря автоматично',
      'Последователна терминология за ЛС, групови чатове и гласови обаждания',
      'Български подсказки и етикети из целия гласов интерфейс и визуализациите на линкове',
      'Превключвател за достъп на асистент до личните съобщения',
      'Отделно състояние „Звъни…" с тон при гласово обаждане',
    ],
    bugfixes: [
      'Изтриването на потребител вече работи — съдържанието се запазва анонимизирано като „Изтрит потребител"',
      'Собственото ви име вече се показва правилно вместо „User"',
      'Маркирането на всички известия като прочетени вече работи',
      'Неуспешно създаване на ЛС вече показва съобщение за грешка вместо да мълчи',
      'По-надеждна защита на личните съобщения и частните канали',
      'По-ясни съобщения при грешка, без разкриване на технически детайли',
    ],
  },
  {
    version: '0.68',
    date: '2026-03-30T14:00:00+02:00',
    title: 'Гласови обаждания, пренареждане на канали и статистики',
    highlights: [
      'Обаждайте се на колеги директно от личните съобщения',
      'Пренареждайте каналите с drag & drop',
      'Нов раздел за гласова статистика в админ панела',
    ],
    features: [
      {
        title: 'Гласови обаждания в ЛС',
        description: 'Обаждайте се директно на потребители от личните съобщения. При входящо обаждане се показва диалог с рингтон, който можете да персонализирате от настройките.',
        icon: '📞',
      },
      {
        title: 'Пренареждане на канали',
        description: 'Подредете каналите в страничния панел по ваш вкус чрез drag & drop. Новата подредба се запазва автоматично.',
        icon: '🔀',
      },
      {
        title: 'Гласова статистика',
        description: 'Администраторите вече виждат подробна статистика за гласовите разговори — времетраене, участници и разбивка по тип сесия.',
        icon: '📊',
      },
      {
        title: '@everyone и @here споменавания',
        description: 'Споменете всички членове на канала с @everyone или само онлайн потребителите с @here.',
        icon: '📢',
      },
    ],
    improvements: [
      'Редактирайте съобщения с Enter за запис и Escape за отказ',
      'Автоматичен фокус на полето за писане при отговор на съобщение',
      'Групово обаждане показва специален етикет в прозореца за разговор',
      'Администраторският панел вече има раздели за AI и гласова статистика',
      'Нов брандинг с обновени икони и фавикон',
      'Групиране на последователни съобщения от един потребител',
      'Асистентите могат да търсят в интернет (настройка за уеб търсене)',
    ],
    bugfixes: [
      'Премахнат дублиран импорт в диалога за входящо обаждане',
      'Подобрено зареждане на прикачени файлове и документи',
      'Коригирано показване на ширината на съобщенията',
    ],
  },
  {
    version: '0.67',
    date: '2026-03-29T18:00:00+02:00',
    title: 'Потребителски профил, споменавания и стабилност',
    highlights: [
      'Натиснете аватар или име за бърз преглед на профила',
      'Споменаванията се показват като нормален текст при писане',
      'По-стабилна връзка при рестарт на сървъра',
    ],
    features: [
      {
        title: 'Профил при натискане',
        description: 'Натиснете аватара или името на потребител в чата, за да видите профила му и да започнете личен разговор.',
        icon: '👤',
      },
      {
        title: 'Подобрени споменавания',
        description: 'При писане на @споменаване виждате нормален текст вместо HTML код. Споменаванията се форматират автоматично при изпращане.',
        icon: '💬',
      },
    ],
    improvements: [
      'Отговорите на асистенти се появяват в реално време без презареждане',
      'Автоматичен фокус на полето за писане при превключване на канал или ЛС',
      'Прикачените файлове и снимки се зареждат по-надеждно',
      'Качените файлове се показват веднага без изчакване',
      'Разгъване на по-стари версии в страницата с история',
    ],
    bugfixes: [
      'Поправено мигане на зоната за пускане на файлове при drag & drop',
      'Приложението вече не ви изкарва при временен проблем със сървъра',
      'Споменаванията се показват правилно в прегледи на ЛС, отговори и търсене',
      'Коригирана точност на таймера за гласови записи',
      'Препратените съобщения вече показват линкове правилно',
    ],
  },
  {
    version: '0.66',
    date: '2026-03-29T02:00:00+02:00',
    title: 'Препращане на съобщения, групови ЛС и нативен десктоп',
    highlights: [
      'Препращайте съобщения между канали и лични разговори',
      'Групови лични съобщения с 2 и повече участници',
      'Нативна заглавна лента за macOS и Windows десктоп',
    ],
    features: [
      {
        title: 'Препращане на съобщения',
        description: 'Препратете съобщение от един канал или ЛС към друг с опция за коментар. Оригиналното съобщение се показва с контекст.',
        icon: '↪️',
      },
      {
        title: 'Групови лични съобщения',
        description: 'Създайте разговор с 2 или повече участници директно от панела за лични съобщения.',
        icon: '👥',
      },
      {
        title: 'Настройки на гласови стаи',
        description: 'Администраторите могат да редактират и изтриват гласови стаи директно от интерфейса.',
        icon: '⚙️',
      },
    ],
    improvements: [
      'Нативна заглавна лента за macOS (traffic lights) и Windows (overlay)',
      'По-стабилна WebSocket връзка — автоматично възстановяване след обновяване на сесията',
      'Подобрен десктоп интерфейс с поддръжка на drag зони за преместване на прозореца',
    ],
    bugfixes: [
      'Поправено прекъсване на съобщенията в реално време при обновяване на токена',
      'Коригирани аватари и типове при групови ЛС',
      'Поправено закачване на съобщения в панела',
    ],
  },
  {
    version: '0.65',
    date: '2026-03-28T23:30:00+02:00',
    title: 'Гласови стаи, drag & drop и подобрена стабилност',
    highlights: [
      'Независими гласови стаи, отделени от каналите',
      'Плъзнете файлове директно в чата с избор: снимка или файл',
      'По-стабилен панел за онлайн потребители и лични съобщения',
    ],
    features: [
      {
        title: 'Гласови стаи',
        description: 'Създавайте и управлявайте гласови стаи, независими от каналите. Присъединете се с един клик от секцията "Дигитален офис".',
        icon: '🎙️',
      },
      {
        title: 'Drag & Drop със зони',
        description: 'Плъзнете файл върху чата и изберете дали да го изпратите като снимка (с визуализация) или като файл за изтегляне.',
        icon: '📎',
      },
    ],
    improvements: [
      'Панелът за онлайн потребители се обновява в реално време',
      'По-надеждна връзка с гласовите услуги при липса на конфигурация',
      'Десктоп приложението има нативна заглавна лента за macOS и Windows',
      'CI/CD пайплайн с паралелни билдове за Android, macOS и Windows',
    ],
    bugfixes: [
      'Поправен панел за лични съобщения — правилно сортиране и без дублиращ се хедър',
      'Коригирано показване на потребители без зададено име',
    ],
  },
  {
    version: '0.64',
    date: '2026-03-28T18:00:00+02:00',
    title: 'Подредба на канали, кирилица и подобрена стабилност',
    highlights: [
      'Подреждайте каналите по ваш ред с drag-and-drop',
      'Задайте канал по подразбиране за нови потребители',
      'Пълна поддръжка на кирилица в имена на канали и потребители',
      'Подобрени известия и индикатори за непрочетени съобщения',
    ],
    features: [
      {
        title: 'Подредба на канали',
        description: 'Администраторите могат да подреждат каналите в произволен ред. Позицията се запазва за всички потребители.',
        icon: '📋',
      },
      {
        title: 'Канал по подразбиране',
        description: 'Задайте кой канал да се отваря автоматично при вход. Настройва се от настройките на канала (само за админи).',
        icon: '🏠',
      },
      {
        title: 'Преработен страничен панел',
        description: 'Десният панел е реорганизиран с табове за членове, медия и търсене — по-бързо навигиране и по-чист интерфейс.',
        icon: '📐',
      },
    ],
    improvements: [
      'Пълна поддръжка на кирилица в имена на канали и потребители',
      'Подобрени известия в реално време и индикатори за непрочетени съобщения',
      'По-стабилно доставяне на push известия',
      'Оптимизирана обработка на прикачени файлове',
    ],
    bugfixes: [
      'Коригиран срив в админ панела при потребител без показвано име',
      'Поправена грешка при компилация от неизползвана променлива',
      'Подобрена стабилност на WebSocket връзката и синхронизацията на съобщения',
    ],
  },
  {
    version: '0.63',
    date: '2026-03-28T15:30:00+02:00',
    title: 'AI асистенти, GIF-ове от Tenor и визуализация на линкове',
    highlights: [
      'AI ботовете са преименувани на Асистенти с табло за използване',
      'GIF-ове от Tenor с търсене и визуализация',
      'Автоматична визуализация на линкове в съобщенията',
      'Чакащи покани се виждат директно в секция Потребители',
    ],
    features: [
      {
        title: 'AI Асистенти',
        description: 'Системата за ботове е преработена като Асистенти — с ново име, ново табло за използване и по-ясна конфигурация от админ панела.',
        icon: '🤖',
      },
      {
        title: 'GIF-ове от Tenor',
        description: 'Изпращайте GIF-ове директно в чата с търсене от Tenor. Работи със собствен бутон до полето за съобщения.',
        icon: '🎬',
      },
      {
        title: 'Визуализация на линкове',
        description: 'Линковете в съобщенията автоматично показват заглавие, описание и снимка от сайта. Поддържа се широк набор от домейни.',
        icon: '🔗',
      },
      {
        title: 'Чакащи покани',
        description: 'Администраторите виждат изпратените покани директно в секцията Потребители — с таб "Чакащи одобрение" за по-лесно управление.',
        icon: '📨',
      },
    ],
    improvements: [
      'Разширен списък с разпознавани домейни за линкове (EU, Tech, популярни TLD)',
      'Реакцията с ❤️ е преместена в лентата за действия за по-удобен достъп',
      'Подобрено възпроизвеждане на гласови съобщения с по-надеждно зареждане',
      'Тултипове при реакции показват кой е реагирал',
      'По-стабилно стартиране на десктоп приложението',
    ],
    bugfixes: [
      'Коригирана грешка 500 при създаване на покана за съществуващ имейл',
      'Поправени файлови пътища в Electron билда (.cjs разширения)',
      'Коригирана продължителност на гласовите съобщения при WebM формат',
    ],
  },
  {
    version: '0.62',
    date: '2026-03-28T22:00:00+02:00',
    title: 'AI ботове, уебхукове и подобрения',
    highlights: [
      'AI ботове, които отговарят на споменавания в каналите',
      'Входящи уебхукове за интеграции с външни услуги',
      'Нова реакция с сърце директно до съобщенията',
      'Двустъпковото удостоверяване е активирано',
    ],
    features: [
      {
        title: 'AI ботове',
        description: 'Споменете @assistant или @research в канал и получете отговор от AI. Ботовете могат да се настройват от админ панела — модел, промпт и канали.',
        icon: '🤖',
      },
      {
        title: 'Входящи уебхукове',
        description: 'Свържете външни услуги (GitLab, мониторинг и др.) с каналите чрез уебхукове. Съобщенията се публикуват автоматично с настройваем шаблон.',
        icon: '🔗',
      },
      {
        title: 'Реакция с сърце',
        description: 'Бърза реакция с двойно натискане или бутон до всяко съобщение. Заменя предишната лента с емотикони.',
        icon: '❤️',
      },
      {
        title: 'AI настройки в админ панела',
        description: 'Конфигурирайте OpenAI API ключ и тествайте връзката директно от админ панела без нужда от .env файлове.',
        icon: '⚙️',
      },
    ],
    improvements: [
      'Двустъпковото удостоверяване по имейл е активирано за всички потребители',
      'Ботовете се показват с BOT етикет в споменавания и съобщения',
      'Опростен профил — само показвано име вместо три полета',
      'Настройката за поверителност на лични съобщения е премахната (неизползвана)',
    ],
    bugfixes: [
      'Оправен бял екран при презареждане на страницата',
      'Коригирана продължителност на гласовите съобщения',
      'Подобрена стабилност на билда за десктоп приложението',
    ],
  },
  {
    version: '0.61',
    date: '2026-03-28T14:00:00+02:00',
    title: 'Нов визуален стил и двустъпково удостоверяване',
    highlights: [
      'Изцяло нов дизайн на страниците за вход и регистрация',
      'Двустъпково удостоверяване с код по имейл',
      'SMTP настройки директно от админ панела',
      'Голям текст при поставяне автоматично се прикачва като файл',
    ],
    features: [
      {
        title: 'Двустъпково удостоверяване (2FA)',
        description: 'При вход се изпраща 6-цифрен код на имейла ви. Доверени устройства запомнят верификацията за 30 дни. Управлявайте устройствата от настройки.',
        icon: '🔐',
      },
      {
        title: 'Нов дизайн на входните страници',
        description: 'Страниците за вход, регистрация и покана са преработени с модерен тъмен стил и ефект на прозрачно стъкло.',
        icon: '🎨',
      },
      {
        title: 'SMTP настройки в админ панела',
        description: 'Конфигурирайте имейл сървъра директно от интерфейса — тествайте връзката преди запазване, без нужда от редактиране на .env файлове.',
        icon: '📧',
      },
      {
        title: 'Голям текст като прикачен файл',
        description: 'Поставяне на дълъг текст или код автоматично го превръща в .txt файл. Прагът е настройваем от Настройки → Външен вид.',
        icon: '📋',
      },
    ],
    improvements: [
      'Панелът с членове на канала се отваря автоматично при избор на канал',
      'Админ панелът е преработен като цял екран с навигация отстрани',
      'Банер за инсталиране на приложението когато сте в браузър',
      'По-чист и подреден интерфейс с подобрени цветове и типография',
    ],
    bugfixes: [
      'Коригиран проблем с паролата за база данни при деплой',
      'Оправен бъг при показване на тост за нова версия',
    ],
  },
  {
    version: '0.6',
    date: '2026-03-27T22:00:00+02:00',
    title: 'Мулти-платформа и групови споменавания',
    highlights: [
      'HighFive вече работи като десктоп и мобилно приложение',
      'Споменавайте цели групи наведнъж с @група',
      'Клавишни комбинации за бързо навигиране',
      'Известия за Android чрез Firebase',
    ],
    features: [
      {
        title: 'Десктоп и мобилно приложение',
        description: 'Освен в браузъра, HighFive вече може да се инсталира като десктоп приложение (Windows/macOS) и мобилно приложение (Android) с автоматични обновявания.',
        icon: '💻',
      },
      {
        title: 'Групови споменавания',
        description: 'Създавайте групи от хора и споменавайте ги наведнъж с @група. Поддръжка и на @here за всички онлайн в канала.',
        icon: '👥',
      },
      {
        title: 'Клавишни комбинации',
        description: 'Ctrl+F за търсене, Ctrl+B/I/E за форматиране, Ctrl+N за известия, Ctrl+? за списък с всички комбинации.',
        icon: '⌨️',
      },
      {
        title: 'Push известия за Android',
        description: 'Мобилното приложение получава известия чрез Firebase дори когато е затворено.',
        icon: '🔔',
      },
    ],
    improvements: [
      'Приложението работи по-надеждно на различни устройства и платформи',
      'По-точно разпознаване кой е онлайн при @here споменавания',
      'Коригирани URL адреси за сървъра за обновявания',
      'Добавена документация за Firebase и VAPID настройки',
    ],
    bugfixes: [
      'Почистени над 160 остарели файла с документация',
      'Оправено игнориране на временни файлове от системата за контрол',
    ],
  },
  {
    version: '0.5',
    date: '2026-02-04T22:45:00+02:00',
    title: 'Дигитален офис и подобрения',
    highlights: [
      'Преработен "Дигитален офис" с Discord-подобна визуализация',
      'Real-time статус индикатори за гласови канали',
      'Показване на работно време на участниците',
      'Подобрена SMTP обработка за покани',
    ],
    features: [
      {
        title: 'Дигитален офис редизайн',
        description: 'Виждайте точно кой е онлайн в гласовите канали с богати статус индикатори (говорене, mute, deafen, споделяне на екран) и счетчик на работно време.',
        icon: '🏢',
      },
      {
        title: 'Индикатори за участници',
        description: 'Анимирани статус индикатори показват дали потребителят говори, е заглушен или споделя екран.',
        icon: '👥',
      },
      {
        title: 'Работно време',
        description: 'Автоматично проследяване на времето прекарано в гласови канали, обновяващо се всяка секунда.',
        icon: '⏱️',
      },
    ],
    improvements: [
      'По-стабилна обработка на SMTP грешки при изпращане на покани',
      'Нов панел за SMTP статус в админ интерфейса',
      'Подобрени съобщения за грешки при покани',
    ],
    bugfixes: [
      'Оправено зареждане на login страница в автоматични тестове',
      'Подобрена обработка на грешки при недостъпен SMTP сървър',
    ],
  },
  {
    version: '0.4',
    date: '2026-02-04T18:00:00+02:00',
    title: 'Стабилност и подобрения',
    highlights: [
      'Подобрена стабилност на бекенда',
      'По-надеждна WebSocket връзка',
      'Оптимизирани настройки',
    ],
    improvements: [
      'По-стабилна обработка на грешки в реално време',
      'Подобрено управление на потребителските сесии',
      'Оптимизирано зареждане на потребителските настройки',
    ],
    bugfixes: [
      'Оправен проблем с прекъсване на връзката при множество устройства',
      'Коригирано поведение при рестартиране на сървъра',
      'Подобрена стабилност при бърза смяна на канали',
    ],
  },
  {
    version: '0.3',
    date: '2026-02-02T16:30:00+02:00',
    title: 'Автоматични обновявания',
    highlights: [
      'Автоматично известие при нова версия',
      'Не е нужно ръчно опресняване',
    ],
    features: [
      {
        title: 'Push обновявания',
        description: 'При деплойване на нова версия, всички потребители автоматично получават известие с бутон за обновяване.',
        icon: '🚀',
      },
    ],
  },
  {
    version: '0.2',
    date: '2026-02-02T14:00:00+02:00',
    title: 'Известия и настройки на канали',
    highlights: [
      'Звукови известия за нови съобщения',
      'Настройки на канали достъпни от сайдбара',
      'Оправени гласови съобщения',
    ],
    features: [
      {
        title: 'Звукови известия',
        description: 'Чувате приятен звук при получаване на нови съобщения от други потребители.',
        icon: '🔔',
      },
      {
        title: 'Настройки на канали',
        description: 'Натиснете иконата за настройки до името на канала за редактиране, архивиране или изтриване.',
        icon: '⚙️',
      },
    ],
    improvements: [
      'Бутони + за добавяне в секциите на сайдбара',
      'Автоматично обновяване на непрочетени съобщения',
    ],
    bugfixes: [
      'Оправен проблем с изпращане на гласови съобщения',
      'Коригирано показване на брояча за непрочетени съобщения',
    ],
  },
  {
    version: '0.1',
    date: '2026-02-02T10:00:00+02:00',
    title: 'Първа версия на HighFive',
    highlights: [
      'Пълна преработка на интерфейса',
      'Нова организация на сайдбара',
      'Подобрени гласови функции',
    ],
    features: [
      {
        title: 'Ново оформление на сайдбара',
        description: 'Преструктуриран сайдбар с разгъващи се секции за канали, лични съобщения и гласови канали.',
        icon: '📱',
      },
      {
        title: 'Панел за записи',
        description: 'Записите от гласови разговори вече са достъпни от дясната странична лента.',
        icon: '🎥',
      },
      {
        title: 'Бърз контрол на статуса',
        description: 'Променяйте статуса си (онлайн, отсъстващ, не безпокойте) директно от потребителския панел.',
        icon: '🟢',
      },
      {
        title: 'Споделяне на екран',
        description: 'Споделяйте екрана си по време на гласови разговори с опция за включване на аудио.',
        icon: '🖥️',
      },
    ],
    improvements: [
      'Подобрена визуална йерархия и spacing в целия интерфейс',
      'Плавни анимации при отваряне/затваряне на секции',
      'Оптимизирано разположение на контролите за mute/deafen',
    ],
    bugfixes: [
      'Оправен проблем с кликането на канали',
      'Подобрена стабилност на WebSocket връзката',
      'Коригирани проблеми с тъмната тема',
    ],
  },
];

export const getLatestVersion = (): ChangelogEntry | undefined => {
  return changelog[0];
};

export const getVersionByNumber = (version: string): ChangelogEntry | undefined => {
  return changelog.find(entry => entry.version === version);
};
